// @ utilities
import { getLanguage, formatText } from 'core/i18n';
// @ constants
const { PERCENTAGE_SYMBOL } = require('constants/RAConstants/index');
const { CURRENCIES } = require('constants/currencies');

const formatPercentage = (percentage = 0, showSymbol = true) => {
    let amount = Math.round(percentage * 100);

    if (showSymbol) {
        amount += PERCENTAGE_SYMBOL;
    }

    return amount;
};

const formatWithoutRoundedPercentage = (percentage = 0, showSymbol = true) => {
    let amount = percentage * 100;

    if (showSymbol) {
        amount += PERCENTAGE_SYMBOL;
    }

    return amount;
};

const privateFormatAmount = (number, decimalLength = 2, sectionDelimiter = '.', decimalLimiter = ',') => {
    let parsedNumber = parseFloat(number);

    if (parsedNumber !== number && number) {
        parsedNumber = parseFloat(number.replace(decimalLimiter, sectionDelimiter));
    }
    if (!isNaN(parsedNumber)) {
        const re = '\\d(?=(\\d{' + 3 + '})+' + (decimalLength > 0 ? '\\D' : '$') + ')';
        const num = parsedNumber.toFixed(Math.max(0, ~~decimalLength));

        return num.replace('.', decimalLimiter).replace(new RegExp(re, 'g'), '$&' + (sectionDelimiter || ','));
    } else {
        return '';
    }
};

const getPlainAmountObject = amountObject => {
    let plainAmountObject;

    if (amountObject.toJS) {
        plainAmountObject = amountObject.toJS();
    } else {
        plainAmountObject = amountObject;
    }

    return plainAmountObject;
};

const formatCurrency = code => {
    return CURRENCIES[code] || code;
};

const formatAmount = (number, lang = getLanguage(), decimals = 2) => {
    switch (lang) {
        case 'en':
            return privateFormatAmount(number, decimals, ',', '.');
        case 'es':
        default:
            return privateFormatAmount(number, decimals, '.', ',');
    }
};

const getCurrencyTranslation = currency => {
    return currency ? formatText('currency-' + currency) : '';
};

const formatAmountObject = (amountObject, isPlainAmountObject = false) => {
    const plainAmountObject = isPlainAmountObject ? amountObject : getPlainAmountObject(amountObject);

    const currencySymbol = formatCurrency(plainAmountObject.currency);
    const amount = formatAmount(plainAmountObject.amount, undefined, plainAmountObject.decimalsLength);
    const currencyText = getCurrencyTranslation(plainAmountObject.currency);

    return {
        amount,
        currencyText,
        currencySymbol,
        currencyCode: plainAmountObject.currency,
    };
};

const formatPercentageWithDecimals = (percentage = 0, showSymbol = true) => {
    let amount = formatAmount(percentage * 100, null, 2);

    if (showSymbol) {
        amount += ' ' + PERCENTAGE_SYMBOL;
    }

    if (Math.abs(amount) < 0.005) return 0;

    return amount;
};

const getFormattedString = (amountObject, truncate = true) => {
    return (
        formatAmountObject(amountObject, false, truncate).amount +
        ' ' +
        formatAmountObject(amountObject, false, truncate).currencySymbol
    );
};

const formatWithoutRound = (amount, maxDecimals, language = getLanguage()) => {
    const amountNumber = Number(amount);

    if (isNaN(amountNumber)) {
        return amount;
    }

    const amountText = amountNumber + '';

    const thousandSeparator = language === 'en' ? ',' : '.';
    const decimalSeparator = language === 'en' ? '.' : ',';

    const units = amountText.split('.');

    const thousands = units[0];
    const thousandLength = thousands.length;

    const formattedThousands = thousands.split('').reduceRight((result, current, index) => {
        const cursor = index + 1 - thousandLength;

        if (current !== '-' && cursor !== 0 && cursor % 3 == 0) {
            return current + thousandSeparator + result;
        } else {
            return current + result;
        }
    }, '');

    const decimals = units[1] || '';

    if (maxDecimals > 0) {
        const limitedDecimals = decimals.substr(0, maxDecimals);

        if (limitedDecimals.length < maxDecimals) {
            const extraDecimals = Array(maxDecimals - limitedDecimals.length + 1).join('0');
            return formattedThousands + decimalSeparator + limitedDecimals + extraDecimals;
        } else {
            return formattedThousands + decimalSeparator + limitedDecimals;
        }
    } else if (maxDecimals === undefined && decimals) {
        return formattedThousands + decimalSeparator + decimals;
    } else {
        return formattedThousands;
    }
};

export default {
    formatAmount,
    formatAmountObject,
    formatPercentage,
    formatPercentageWithDecimals,
    formatWithoutRound,
    formatWithoutRoundedPercentage,
    getPlainAmountObject,
    getFormattedString,
};
